// BancosView — lista de bancos do professor com filtros, upload JSON, edit, archive, delete

import { signal } from "@preact/signals";
import { useEffect, useMemo, useState } from "preact/hooks";
import {
  AlertCircle,
  Archive,
  ArchiveRestore,
  Check,
  Database,
  Globe,
  Loader2,
  Lock,
  Pencil,
  PencilLine,
  Plus,
  Save,
  Search,
  Shield,
  Trash2,
  Upload,
  X
} from "lucide-react";
import { api, ApiError, type BankRow } from "../api";
import { openBankInEditor } from "../App";
import { openAlert } from "../ConfirmPromptModal";
import { BankGrantsModal } from "./BankGrantsModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

const banks = signal<BankRow[] | null>(null);
const phase = signal<"loading" | "ready" | "error">("loading");
const errMsg = signal<string | null>(null);
const search = signal<string>("");
const filterStatus = signal<"all" | "active" | "archived">("active");
const editing = signal<BankRow | null>(null);
const deleting = signal<BankRow | null>(null);
const uploading = signal<BankRow | "new" | null>(null);
const granting = signal<BankRow | null>(null);

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const r = await api.listBanks();
    banks.value = r.banks;
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(e);
    phase.value = "error";
  }
}

export function BancosView() {
  useEffect(() => {
    load();
  }, []);
  if (phase.value === "loading") {
    return (
      <p class="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Carregando…
      </p>
    );
  }
  if (phase.value === "error") {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" />
        {errMsg.value}
      </div>
    );
  }
  const rows = banks.value ?? [];
  const q = search.value.toLowerCase().trim();
  const fs = filterStatus.value;
  const visible = useMemo(() => {
    return rows.filter((b) => {
      if (fs === "active" && b.archivedAt) return false;
      if (fs === "archived" && !b.archivedAt) return false;
      if (q) {
        const hay = `${b.name} ${b.description ?? ""} ${b.tags.join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, q, fs]);

  const counts = useMemo(() => {
    let active = 0, archived = 0;
    for (const b of rows) (b.archivedAt ? archived++ : active++);
    return { active, archived, total: rows.length };
  }, [rows]);

  return (
    <div class="space-y-4">
      <header class="flex items-start justify-between gap-3">
        <div>
          <h1 class="text-2xl font-bold tracking-tight">Meus bancos</h1>
          <p class="mt-1 text-sm text-muted-foreground">
            {rows.length} banco{rows.length !== 1 ? "s" : ""} no total — visíveis ou
            arquivados.
          </p>
        </div>
        <button
          type="button"
          onClick={() => (uploading.value = "new")}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110"
        >
          <Plus width="1.1em" height="1.1em" />
          Novo banco
        </button>
      </header>

      <div class="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card/40 px-3 py-2">
        <div class="relative min-w-[240px] flex-1">
          <Search
            width="1em"
            height="1em"
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            placeholder="Buscar nome, descrição, tag…"
            value={search.value}
            onInput={(e) => (search.value = (e.target as HTMLInputElement).value)}
            class="w-full rounded-md border border-border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </div>
        {(["active", "archived", "all"] as const).map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => (filterStatus.value = opt)}
            class={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors ${
              fs === opt
                ? "border-primary/50 bg-primary/10 text-primary"
                : "border-border bg-background/50 text-muted-foreground hover:bg-accent"
            }`}
          >
            {opt === "active" ? "Ativos" : opt === "archived" ? "Arquivados" : "Todos"}
            <span class="rounded bg-accent/60 px-1 text-[10px] tabular-nums">
              {opt === "active" ? counts.active : opt === "archived" ? counts.archived : counts.total}
            </span>
          </button>
        ))}
      </div>

      <p class="px-1 text-xs text-muted-foreground">
        {visible.length} de {rows.length}
      </p>

      {visible.length === 0 ? (
        <div class="rounded-md border border-dashed border-border bg-card/30 px-4 py-8 text-center text-sm text-muted-foreground">
          Nenhum banco encontrado.
        </div>
      ) : (
        <div class="overflow-hidden rounded-md border border-border bg-card/40">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-border bg-card/60 text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                <th class="px-3 py-2">Banco</th>
                <th class="px-3 py-2 text-right">Q</th>
                <th class="px-3 py-2 text-right">v</th>
                <th class="px-3 py-2">Status</th>
                <th class="px-3 py-2 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((b) => (
                <BankListRow key={b.id} bank={b} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing.value && <EditModal bank={editing.value} />}
      {deleting.value && <DeleteModal bank={deleting.value} />}
      {uploading.value && <UploadModal target={uploading.value} />}
      {granting.value && (
        <BankGrantsModal
          bank={{ id: granting.value.id, name: granting.value.name }}
          onClose={() => (granting.value = null)}
        />
      )}
    </div>
  );
}

function BankListRow({ bank }: { bank: BankRow }) {
  const isArchived = !!bank.archivedAt;
  return (
    <tr
      class={`border-b border-border/60 transition-colors last:border-0 hover:bg-accent/30 ${
        isArchived ? "opacity-60" : ""
      }`}
    >
      <td class="px-3 py-2">
        <div class="flex items-center gap-1.5 font-semibold">
          {isArchived && (
            <Archive width="0.9em" height="0.9em" className="text-amber-600 dark:text-amber-400" />
          )}
          {bank.name}
        </div>
        {bank.description && (
          <div class="line-clamp-1 text-[11px] text-muted-foreground">{bank.description}</div>
        )}
      </td>
      <td class="px-3 py-2 text-right tabular-nums font-bold">{bank.questionCount}</td>
      <td class="px-3 py-2 text-right tabular-nums text-xs text-muted-foreground">v{bank.version}</td>
      <td class="px-3 py-2">
        <span
          class={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
            isArchived
              ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
              : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
          }`}
        >
          {isArchived ? "Arquivado" : "Ativo"}
        </span>
      </td>
      <td class="px-3 py-2 text-right">
        <div class="inline-flex items-center gap-1">
          <button
            type="button"
            onClick={() => openBankInEditor(bank.id)}
            title="Editar questões deste banco"
            aria-label="Editar questões"
            class="inline-flex items-center gap-1 rounded-md border border-primary/40 bg-primary/5 px-2 py-1 text-xs font-bold text-primary hover:bg-primary/10"
          >
            <PencilLine width="0.95em" height="0.95em" />
            Questões
          </button>
          <button
            type="button"
            onClick={() => (uploading.value = bank)}
            title="Substituir conteúdo do banco (upload de JSON inteiro)"
            aria-label="Upload novo payload"
            class="rounded-md border border-border bg-background px-2 py-1 text-xs hover:bg-accent"
          >
            <Upload width="0.95em" height="0.95em" />
          </button>
          <button
            type="button"
            onClick={() => (granting.value = bank)}
            title="Permissões: liberar banco pra alunos das minhas turmas"
            aria-label="Permissões"
            class="rounded-md border border-border bg-background px-2 py-1 text-xs hover:bg-accent"
          >
            <Shield width="0.95em" height="0.95em" />
          </button>
          <button
            type="button"
            onClick={() => (editing.value = bank)}
            title="Editar metadados (nome, descrição, tags)"
            aria-label="Editar metadados"
            class="rounded-md border border-border bg-background px-2 py-1 text-xs hover:bg-accent"
          >
            <Pencil width="0.95em" height="0.95em" />
          </button>
          <button
            type="button"
            onClick={() => (deleting.value = bank)}
            title="Excluir"
            aria-label="Excluir"
            class="rounded-md border border-red-500/40 bg-red-500/5 px-2 py-1 text-xs text-red-600 hover:bg-red-500/10 dark:text-red-400"
          >
            <Trash2 width="0.95em" height="0.95em" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function EditModal({ bank }: { bank: BankRow }) {
  const [name, setName] = useState(bank.name);
  const [description, setDescription] = useState(bank.description ?? "");
  const [isPublic, setIsPublic] = useState(bank.isPublicToCompany);
  const [tags, setTags] = useState<string[]>(bank.tags);
  const [tagDraft, setTagDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const archived = !!bank.archivedAt;

  async function save() {
    setSaving(true);
    try {
      await api.patchBank(bank.id, {
        name: name.trim(),
        description: description.trim() || null,
        isPublicToCompany: isPublic,
        tags
      });
      editing.value = null;
      await load();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setSaving(false);
    }
  }
  async function toggleArchive() {
    setSaving(true);
    try {
      await api.patchBank(bank.id, { archived: !archived });
      editing.value = null;
      await load();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setSaving(false);
    }
  }
  function addTag() {
    const t = tagDraft.trim().toLowerCase();
    if (!t || tags.includes(t)) {
      setTagDraft("");
      return;
    }
    setTags([...tags, t]);
    setTagDraft("");
  }

  return (
    <Modal title={`Editar: ${bank.name}`} onClose={() => (editing.value = null)}>
      <Field label="Nome *">
        <input
          type="text"
          value={name}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
          aria-label="Nome do banco"
          class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
        />
      </Field>
      <Field label="Descrição">
        <textarea
          value={description}
          onInput={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
          rows={2}
          aria-label="Descrição do banco"
          placeholder="Opcional"
          class="w-full resize-y rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
        />
      </Field>
      <Field label="Visibilidade">
        <VisibilityPicker value={isPublic} onChange={setIsPublic} />
      </Field>
      <Field label="Tags">
        <div class="flex gap-1.5">
          <input
            type="text"
            value={tagDraft}
            onInput={(e) => setTagDraft((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
            placeholder="namespace:valor"
            aria-label="Nova tag"
            class="flex-1 rounded-md border border-border bg-background px-2 py-1 text-xs outline-none focus:border-primary"
          />
          <button
            type="button"
            onClick={addTag}
            class="rounded-md border border-border bg-background px-3 py-1 text-xs font-semibold hover:bg-accent"
          >
            + Tag
          </button>
        </div>
        {tags.length > 0 && (
          <ul class="mt-2 flex flex-wrap gap-1">
            {tags.map((t) => (
              <li
                key={t}
                class="inline-flex items-center gap-1 rounded bg-accent px-2 py-0.5 text-[11px]"
              >
                <span class="font-mono">{t}</span>
                <button
                  type="button"
                  onClick={() => setTags(tags.filter((x) => x !== t))}
                  title="Remover"
                  aria-label="Remover tag"
                  class="text-muted-foreground hover:text-destructive"
                >
                  <X width="11" height="11" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Field>
      <div class="flex items-center justify-between gap-2 rounded-md border border-border bg-background/50 px-3 py-2 text-xs">
        <span>
          Status atual: <b>{archived ? "Arquivado" : "Ativo"}</b>
        </span>
        <button
          type="button"
          onClick={toggleArchive}
          disabled={saving}
          class={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-bold disabled:opacity-50 ${
            archived
              ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
              : "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
          }`}
        >
          {archived ? (
            <>
              <ArchiveRestore width="0.9em" height="0.9em" /> Reativar
            </>
          ) : (
            <>
              <Archive width="0.9em" height="0.9em" /> Arquivar
            </>
          )}
        </button>
      </div>
      <ModalFooter>
        <button
          type="button"
          onClick={() => (editing.value = null)}
          disabled={saving}
          class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving || !name.trim()}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
        >
          {saving ? <Loader2 className="size-3.5 animate-spin" /> : <Save width="1em" height="1em" />}
          Salvar
        </button>
      </ModalFooter>
    </Modal>
  );
}

function DeleteModal({ bank }: { bank: BankRow }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const ok = text.trim() === bank.name.trim();
  async function go() {
    if (!ok) return;
    setBusy(true);
    try {
      await api.deleteBank(bank.id);
      deleting.value = null;
      await load();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal title="Excluir banco" onClose={() => (deleting.value = null)}>
      <p class="text-sm text-muted-foreground">
        Vai apagar <b class="text-foreground">{bank.name}</b> ({bank.questionCount}{" "}
        questões) + todo o progresso/resultado dos alunos.{" "}
        <b class="text-foreground">Sem volta.</b>
      </p>
      <Field label={`Digite "${bank.name}" pra confirmar`}>
        <input
          type="text"
          value={text}
          onInput={(e) => setText((e.target as HTMLInputElement).value)}
          aria-label="Confirmação"
          class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-destructive"
        />
      </Field>
      <ModalFooter>
        <button
          type="button"
          onClick={() => (deleting.value = null)}
          disabled={busy}
          class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={go}
          disabled={!ok || busy}
          class="inline-flex items-center gap-1.5 rounded-md border border-red-500/50 bg-red-500/10 px-3 py-1.5 text-xs font-bold text-red-600 hover:bg-red-500/15 disabled:opacity-40 dark:text-red-400"
        >
          {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Trash2 width="1em" height="1em" />}
          Excluir
        </button>
      </ModalFooter>
    </Modal>
  );
}

function UploadModal({ target }: { target: BankRow | "new" }) {
  const isNew = target === "new";
  const existing = isNew ? null : (target as BankRow);
  const [name, setName] = useState(existing?.name ?? "");
  const [description, setDescription] = useState(existing?.description ?? "");
  const [isPublic, setIsPublic] = useState(existing?.isPublicToCompany ?? false);
  const [jsonText, setJsonText] = useState("");
  const [parsed, setParsed] = useState<unknown>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function tryParse(raw: string) {
    setJsonText(raw);
    setParseError(null);
    setParsed(null);
    if (!raw.trim()) return;
    try {
      const obj = JSON.parse(raw);
      let questoes: any[] | null = null;
      if (Array.isArray(obj)) questoes = obj;
      else if (obj && Array.isArray(obj.questoes)) questoes = obj.questoes;
      else if (obj && Array.isArray(obj.questions)) questoes = obj.questions;
      if (!questoes) {
        setParseError("JSON precisa ser array de questões ou { questoes: [...] }.");
        return;
      }
      setParsed({ questoes });
    } catch (err) {
      setParseError(err instanceof Error ? `JSON inválido: ${err.message}` : "JSON inválido");
    }
  }
  async function handleFile(e: Event) {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      tryParse(text);
    } catch (err) {
      setParseError(err instanceof Error ? err.message : "Falha ao ler");
    }
  }
  async function go() {
    if (!name.trim() || !parsed) return;
    setBusy(true);
    try {
      if (isNew) {
        await api.createBank({
          name: name.trim(),
          description: description.trim() || null,
          data: parsed,
          isPublicToCompany: isPublic
        });
      } else {
        await api.patchBank(existing!.id, {
          name: name.trim(),
          description: description.trim() || null,
          data: parsed,
          isPublicToCompany: isPublic
        });
      }
      uploading.value = null;
      await load();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setBusy(false);
    }
  }
  const valid = name.trim().length > 0 && parsed != null && !parseError;
  const count =
    parsed && typeof parsed === "object" && Array.isArray((parsed as any).questoes)
      ? ((parsed as any).questoes as any[]).length
      : 0;

  return (
    <Modal
      title={isNew ? "Novo banco" : `Substituir conteúdo — ${existing!.name}`}
      onClose={() => (uploading.value = null)}
      wide
    >
      <Field label="Nome *">
        <input
          type="text"
          value={name}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
          aria-label="Nome do banco"
          class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
        />
      </Field>
      <Field label="Descrição">
        <textarea
          value={description}
          onInput={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
          rows={2}
          aria-label="Descrição"
          placeholder="Opcional"
          class="w-full resize-y rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
        />
      </Field>
      <Field label="JSON do banco">
        <label class="mb-2 inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent">
          <Upload width="1em" height="1em" /> Escolher arquivo .json
          <input
            type="file"
            accept=".json,application/json"
            onChange={handleFile}
            class="hidden"
          />
        </label>
        <textarea
          value={jsonText}
          onInput={(e) => tryParse((e.target as HTMLTextAreaElement).value)}
          rows={6}
          aria-label="Conteúdo JSON"
          placeholder='[{"pergunta": "...", "alternativas": ["A","B","C","D","E"], "correta": 1}]'
          class={`w-full resize-y rounded-md border bg-background px-3 py-1.5 font-mono text-[11px] outline-none focus:border-primary ${
            parseError ? "border-destructive" : "border-border"
          }`}
        />
        {parseError && (
          <p class="mt-1 text-[11px] text-destructive">{parseError}</p>
        )}
        {!parseError && parsed && (
          <p class="mt-1 text-[11px] text-emerald-700 dark:text-emerald-300">
            ✓ {count}{" "}
            {count === 1 ? "questão parseada" : "questões parseadas"}.
          </p>
        )}
      </Field>
      <Field label="Visibilidade">
        <VisibilityPicker value={isPublic} onChange={setIsPublic} />
      </Field>
      <ModalFooter>
        <button
          type="button"
          onClick={() => (uploading.value = null)}
          disabled={busy}
          class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={go}
          disabled={!valid || busy}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
        >
          {busy ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : isNew ? (
            <Plus width="1em" height="1em" />
          ) : (
            <Save width="1em" height="1em" />
          )}
          {isNew ? "Criar" : "Substituir"}
        </button>
      </ModalFooter>
    </Modal>
  );
}

// shared
function Modal({
  title,
  onClose,
  wide,
  children
}: {
  title: string;
  onClose: () => void;
  wide?: boolean;
  children: any;
}) {
  return (
    <div
      class="fixed inset-0 z-50 grid place-items-center bg-background/80 p-3 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        class={`flex max-h-[92vh] w-full ${wide ? "max-w-2xl" : "max-w-lg"} flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <header class="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 class="flex items-center gap-2 text-base font-bold">
            <Database width="1.2em" height="1.2em" className="text-primary" />
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            title="Fechar"
            aria-label="Fechar"
            class="rounded-md border border-border bg-background p-1 hover:bg-accent"
          >
            <X width="1em" height="1em" />
          </button>
        </header>
        <div class="flex-1 space-y-3 overflow-y-auto p-4">{children}</div>
      </div>
    </div>
  );
}
// Seletor visual de visibilidade do banco. Dois cards radio (público vs
// privado) tornam a diferença óbvia — o checkbox antigo dava a entender que
// "desmarcado" era um estado neutro, quando na verdade é o default e tem
// consequência (banco invisível pra Company até receber grant individual).
function VisibilityPicker({
  value,
  onChange
}: {
  value: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div class="grid gap-2 sm:grid-cols-2">
      <VisibilityCard
        active={!value}
        onClick={() => onChange(false)}
        icon={Lock}
        title="Privado"
        desc="Só alunos com acesso explícito (atribuição direta, via disciplina ou turma) veem o banco."
        accent="amber"
      />
      <VisibilityCard
        active={value}
        onClick={() => onChange(true)}
        icon={Globe}
        title="Público para a Company"
        desc="Qualquer aluno da sua organização pode visualizar e estudar este banco."
        accent="emerald"
      />
    </div>
  );
}

function VisibilityCard({
  active,
  onClick,
  icon: Icon,
  title,
  desc,
  accent
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof Globe;
  title: string;
  desc: string;
  accent: "amber" | "emerald";
}) {
  // Cores do estado "active" mudam por accent — privado=âmbar (atenção),
  // público=esmeralda (verde liberação). Inativo é neutro pra evitar
  // disputar atenção visual.
  const activeRing =
    accent === "amber"
      ? "border-amber-500 ring-2 ring-amber-500/30 bg-amber-500/5"
      : "border-emerald-500 ring-2 ring-emerald-500/30 bg-emerald-500/5";
  const activeIcon =
    accent === "amber"
      ? "bg-amber-500/15 text-amber-600 dark:text-amber-300"
      : "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      class={`group flex w-full items-start gap-3 rounded-md border p-3 text-left transition-all ${
        active
          ? activeRing
          : "border-border bg-background hover:border-primary/40 hover:bg-accent/40"
      }`}
    >
      <span
        class={`grid size-9 shrink-0 place-items-center rounded-md ${
          active ? activeIcon : "bg-muted text-muted-foreground"
        }`}
      >
        <Icon className="size-4" />
      </span>
      <span class="min-w-0 flex-1">
        <span class="flex items-center gap-1.5 text-sm font-bold">
          {title}
          {active && (
            <Check
              className={
                accent === "amber"
                  ? "size-3.5 text-amber-600 dark:text-amber-400"
                  : "size-3.5 text-emerald-600 dark:text-emerald-400"
              }
            />
          )}
        </span>
        <span class="mt-0.5 block text-[11px] leading-snug text-muted-foreground">
          {desc}
        </span>
      </span>
    </button>
  );
}

function Field({ label, children }: { label: string; children: any }) {
  return (
    <label class="block">
      <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}
function ModalFooter({ children }: { children: any }) {
  return (
    <div class="sticky bottom-0 -mx-4 -mb-4 mt-2 flex items-center justify-end gap-2 border-t border-border bg-card px-4 py-3">
      {children}
    </div>
  );
}
